import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    FileText, Wrench, Image, BarChart2, Mail, Users, ArrowRight, Check, Sun, Moon, Zap, TrendingUp, Activity, Repeat, Bell, Archive,
    Shield, FileSearch, ShieldCheck, ChevronRight
} from 'lucide-react';
import { usePreferences } from '../contexts/PreferencesContext';
import { useTranslations } from '../i18n';
const favicon = '/favicon_clear.png';
const faviconDark = '/favicon_clear_dark.png';
import { LanguageSelector } from '../components/ui/LanguageSelector';
import { CarLandingPreview } from '../components/landing/CarLandingPreview';
import { RepairLandingPreview } from '../components/landing/RepairLandingPreview';
import { SEO } from '../components/ui/SEO';
import { blogPosts } from '../data/blogPosts';
import { Calendar } from 'lucide-react';

export function LandingPage() {
    const navigate = useNavigate();
    const { preferences, setTheme } = usePreferences();
    const { t, language } = useTranslations();
    const { user } = useAuth();
    const isDark = preferences?.theme === 'dark';
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly');

    return (
        <div className="min-h-screen bg-transparent font-primary text-slate-900 dark:text-slate-100 flex flex-col">
            <SEO />
            {/* Header */}
            <header className="sticky top-0 z-50 w-full border-b border-slate-200 dark:border-slate-800 bg-[#EEF5FB]/80 dark:bg-[#000D19]/80 backdrop-blur-md">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img
                            src={isDark ? faviconDark : favicon}
                            alt="Logo"
                            className="w-8 h-8 object-contain"
                        />
                        <span className="text-xl font-bold tracking-tight">My Car Costs</span>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 mr-2">
                            <LanguageSelector variant="ghost" useModal={true} />
                            <button
                                onClick={() => setTheme(preferences?.theme === 'dark' ? 'light' : 'dark')}
                                className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-lg transition-colors text-slate-600 dark:text-slate-400"
                                aria-label="Toggle Theme"
                            >
                                {preferences?.theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                            </button>
                        </div>
                        {!user && (
                            <Link
                                to="/login"
                                className="text-sm font-semibold hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                            >
                                {t.landing.nav.login}
                            </Link>
                        )}
                        <button
                            onClick={() => navigate(user ? '/' : '/login')}
                            className="hidden sm:flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all shadow-lg shadow-blue-500/20"
                        >
                            {user ? (t.landing.nav as any).garage : t.landing.nav.getStarted}
                            <ArrowRight size={16} />
                        </button>
                    </div>
                </div>
            </header>

            <main className="flex-1">
                {/* Hero Section */}
                <section className="relative pt-20 lg:pt-32 pb-32 overflow-hidden">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center gap-16 lg:gap-24 relative z-10">
                        {/* Text Content */}
                        <div className="flex-1 flex flex-col items-center lg:items-start text-center lg:text-left">
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-slate-900 via-blue-800 to-slate-900 dark:from-white dark:via-blue-200 dark:to-white pb-2 leading-[1.1]">
                                {t.landing.hero.title} <br className="hidden md:block" />
                                <span className="text-blue-600 dark:text-blue-500">{t.landing.hero.subtitle}</span>
                            </h1>

                            <p className="text-lg md:text-2xl text-slate-600 dark:text-slate-400 max-w-2xl mb-12 leading-relaxed font-medium">
                                {t.landing.hero.description}
                            </p>

                            <div className="flex flex-col sm:flex-row items-center gap-6 w-full sm:w-auto">
                                <button
                                    onClick={() => navigate(user ? '/' : '/login')}
                                    className="w-full sm:w-auto px-10 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-black text-xl transition-all shadow-2xl shadow-blue-500/30 flex items-center justify-center gap-4 hover:scale-105 active:scale-95"
                                >
                                    {user ? (t.landing.nav as any).garage : t.landing.hero.startTracking}
                                    <ArrowRight size={24} />
                                </button>
                                
                                <button 
                                    onClick={() => window.open('/280472aa23', '_blank')}
                                    className="hidden lg:flex items-center gap-2 px-8 py-4 bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm border border-slate-200 dark:border-slate-700 hover:bg-white dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-2xl font-bold transition-all text-lg"
                                >
                                    {t.landing.hero.viewDemo}
                                </button>
                            </div>
                        </div>

                        {/* App Preview Mockup */}
                        <div className="flex-shrink-0 w-full max-w-[320px] sm:max-w-[360px] flex flex-col items-center gap-10">
                            <div className="relative group/phone w-full">
                                <div className="absolute -inset-4 bg-gradient-to-tr from-blue-500 to-purple-600 rounded-[3rem] blur-2xl opacity-20 dark:opacity-30 group-hover/phone:opacity-40 transition-opacity duration-700"></div>
                                
                                <div className="relative rounded-[3rem] border-8 border-slate-900 dark:border-slate-800 bg-gradient-theme shadow-3xl overflow-hidden aspect-[9/19] flex flex-col">
                                    <div className="h-10 px-6 flex items-center justify-between text-[11px] font-black z-30 text-slate-900 dark:text-white pointer-events-none shrink-0 bg-transparent">
                                        <span className="mt-1">23:14</span>
                                        <div className="flex items-center gap-1 mt-1">
                                            <div className="flex gap-0.5 items-end h-2.5">
                                                <div className="w-0.5 h-1 bg-current opacity-40"></div>
                                                <div className="w-0.5 h-1.5 bg-current"></div>
                                                <div className="w-0.5 h-2 bg-current"></div>
                                            </div>
                                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12.55a11 11 0 0 1 14.08 0" /><path d="M1.42 9a16 16 0 0 1 21.16 0" /><path d="M8.53 16.11a6 6 0 0 1 6.95 0" /><line x1="12" y1="20" x2="12.01" y2="20" /></svg>
                                            <div className="w-5 h-2.5 border border-current rounded-[2px] p-[1px] flex items-center">
                                                <div className="flex-1 h-[100%] bg-current rounded-[0.5px]"></div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="absolute top-0 left-0 right-0 h-6 flex justify-center z-40 pointer-events-none">
                                        <div className="w-24 h-5 bg-slate-900 dark:bg-slate-800 rounded-b-2xl"></div>
                                    </div>
                                    
                                    <div className="flex-1 overflow-hidden">
                                        <CarLandingPreview slug="280472aa23" />
                                    </div>
                                    
                                    <div className="absolute inset-0 pointer-events-none select-none z-10 bg-gradient-to-tr from-white/5 via-transparent to-white/5"></div>
                                    
                                    <div className="absolute bottom-1.5 left-0 right-0 h-1 flex justify-center z-30">
                                        <div className="w-1/3 h-1 bg-slate-400/20 rounded-full"></div>
                                    </div>
                                </div>
                            </div>

                            <button 
                                onClick={() => window.open('/280472aa23', '_blank')}
                                className="lg:hidden flex items-center gap-2 px-6 py-3 bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm border border-slate-200 dark:border-slate-700 hover:bg-white dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-xl font-bold transition-all"
                            >
                                {t.landing.hero.viewDemo}
                            </button>
                        </div>
                    </div>

                    {/* Background decoration */}
                    <div className="absolute top-0 inset-x-0 h-full overflow-hidden -z-10 pointer-events-none">
                        <div className="absolute left-[calc(50%-15rem)] top-10 w-[50rem] h-[50rem] bg-gradient-to-tr from-blue-100 to-purple-100 dark:from-blue-900/20 dark:to-purple-900/20 rounded-full blur-3xl opacity-60"></div>
                        <div className="absolute right-[calc(50%-20rem)] top-[20rem] w-[40rem] h-[40rem] bg-gradient-to-bl from-teal-100 to-blue-100 dark:from-teal-900/20 dark:to-blue-900/20 rounded-full blur-3xl opacity-60"></div>
                    </div>
                </section>

                {/* Strategic Benefits Section */}
                <section className="py-24 bg-white dark:bg-[#000408] relative border-b border-slate-100 dark:border-slate-900">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid md:grid-cols-3 gap-12">
                            <div className="flex flex-col gap-6 p-10 rounded-[3rem] bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800 hover:shadow-2xl transition-all duration-500 group">
                                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/40 rounded-2xl flex items-center justify-center text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform shadow-sm">
                                    <TrendingUp size={32} />
                                </div>
                                <div className="space-y-4">
                                    <h3 className="text-2xl font-black text-slate-900 dark:text-white leading-tight">
                                        {t.landing.hero.benefits.repairVsSell.title}
                                    </h3>
                                    <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                                        {t.landing.hero.benefits.repairVsSell.description}
                                    </p>
                                </div>
                            </div>

                            <div className="flex flex-col gap-6 p-10 rounded-[3rem] bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800 hover:shadow-2xl transition-all duration-500 group">
                                <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/40 rounded-2xl flex items-center justify-center text-indigo-600 dark:text-indigo-400 group-hover:scale-110 transition-transform shadow-sm">
                                    <Activity size={32} />
                                </div>
                                <div className="space-y-4">
                                    <h3 className="text-2xl font-black text-slate-900 dark:text-white leading-tight">
                                        {t.landing.hero.benefits.costPerKm.title}
                                    </h3>
                                    <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                                        {t.landing.hero.benefits.costPerKm.description}
                                    </p>
                                </div>
                            </div>

                            <div className="flex flex-col gap-6 p-10 rounded-[3rem] bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800 hover:shadow-2xl transition-all duration-500 group">
                                <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/40 rounded-2xl flex items-center justify-center text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform shadow-sm">
                                    <Zap size={32} />
                                </div>
                                <div className="space-y-4">
                                    <h3 className="text-2xl font-black text-slate-900 dark:text-white leading-tight">
                                        {t.landing.hero.benefits.automation.title}
                                    </h3>
                                    <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                                        {t.landing.hero.benefits.automation.description}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <section id="who-this-is-for" className="py-24">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <h2 className="text-3xl md:text-5xl font-black mb-4 tracking-tight">{t.landing.useCases.title}</h2>
                        </div>

                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
                            <div className="p-10 bg-slate-50 dark:bg-slate-900/50 rounded-[3rem] border border-slate-100 dark:border-slate-800 shadow-sm hover:shadow-2xl transition-all duration-500 group">
                                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/40 rounded-2xl flex items-center justify-center text-blue-600 dark:text-blue-400 mb-8 group-hover:scale-110 transition-transform">
                                    <Zap size={32} />
                                </div>
                                <h3 className="text-2xl font-black mb-4 text-slate-900 dark:text-white leading-tight">{t.landing.useCases.enthusiasts.title}</h3>
                                <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                                    {t.landing.useCases.enthusiasts.description}
                                </p>
                            </div>
                            <div className="p-10 bg-slate-50 dark:bg-slate-900/50 rounded-[3rem] border border-slate-100 dark:border-slate-800 shadow-sm hover:shadow-2xl transition-all duration-500 group">
                                <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/40 rounded-2xl flex items-center justify-center text-emerald-600 dark:text-emerald-400 mb-8 group-hover:scale-110 transition-transform">
                                    <Activity size={32} />
                                </div>
                                <h3 className="text-2xl font-black mb-4 text-slate-900 dark:text-white leading-tight">{t.landing.useCases.dailyDrivers.title}</h3>
                                <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                                    {t.landing.useCases.dailyDrivers.description}
                                </p>
                            </div>
                            <div className="p-10 bg-slate-50 dark:bg-slate-900/50 rounded-[3rem] border border-slate-100 dark:border-slate-800 shadow-sm hover:shadow-2xl transition-all duration-500 group">
                                <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/40 rounded-2xl flex items-center justify-center text-purple-600 dark:text-purple-400 mb-8 group-hover:scale-110 transition-transform">
                                    <TrendingUp size={32} />
                                </div>
                                <h3 className="text-2xl font-black mb-4 text-slate-900 dark:text-white leading-tight">{t.landing.useCases.flippers.title}</h3>
                                <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                                    {t.landing.useCases.flippers.description}
                                </p>
                            </div>
                            <div className="p-10 bg-slate-50 dark:bg-slate-900/50 rounded-[3rem] border border-slate-100 dark:border-slate-800 shadow-sm hover:shadow-2xl transition-all duration-500 group">
                                <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/40 rounded-2xl flex items-center justify-center text-orange-600 dark:text-orange-400 mb-8 group-hover:scale-110 transition-transform">
                                    <Users size={32} />
                                </div>
                                <h3 className="text-2xl font-black mb-4 text-slate-900 dark:text-white leading-tight">{t.landing.useCases.fleets.title}</h3>
                                <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                                    {t.landing.useCases.fleets.description}
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Features Grid */}
                <section id="features" className="py-24 border-y border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <h2 className="text-3xl md:text-4xl font-black mb-4">{t.landing.features.title}</h2>
                            <p className="text-lg text-slate-600 dark:text-slate-400 font-medium">
                                {t.landing.features.subtitle}
                            </p>
                        </div>

                        <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-6">
                            <FeatureCard
                                icon={<BarChart2 size={24} />}
                                title={t.landing.features.expenseTracking.title}
                                description={t.landing.features.expenseTracking.description}
                                color="blue"
                            />
                            <FeatureCard
                                icon={<Wrench size={24} />}
                                title={t.landing.features.spareParts.title}
                                description={t.landing.features.spareParts.description}
                                color="orange"
                            />
                            <FeatureCard
                                icon={<FileText size={24} />}
                                title={t.landing.features.serviceHistory.title}
                                description={t.landing.features.serviceHistory.description}
                                color="purple"
                            />
                            <FeatureCard
                                icon={<Image size={24} />}
                                title={t.landing.features.documentStorage.title}
                                description={t.landing.features.documentStorage.description}
                                color="green"
                            />
                            <FeatureCard
                                icon={<Mail size={24} />}
                                title={t.landing.features.emailForwarding.title}
                                description={t.landing.features.emailForwarding.description}
                                color="red"
                            />
                            <FeatureCard
                                icon={<Users size={24} />}
                                title={t.landing.features.multiCar.title}
                                description={t.landing.features.multiCar.description}
                                color="indigo"
                            />
                            <FeatureCard
                                icon={<Repeat size={24} />}
                                title={(t.landing.features as any).repeatingExpenses.title}
                                description={(t.landing.features as any).repeatingExpenses.description}
                                color="cyan"
                            />
                            <FeatureCard
                                icon={<Bell size={24} />}
                                title={(t.landing.features as any).smartReminders.title}
                                description={(t.landing.features as any).smartReminders.description}
                                color="yellow"
                            />
                        </div>
                    </div>
                </section>

                {/* Pricing Section */}
                <section id="pricing" className="py-24">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center max-w-3xl mx-auto mb-10">
                            <h2 className="text-3xl font-bold mb-4">{t.landing.pricing.title}</h2>
                            <p className="text-lg text-slate-600 dark:text-slate-400">
                                {t.landing.pricing.subtitle}
                            </p>
                        </div>

                        {/* Pricing Wrapper */}
                        <div className="mt-8">
                            <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
                                {/* Free Plan */}
                                <div className="border border-slate-200 dark:border-slate-700 rounded-2xl p-8 bg-white dark:bg-slate-900 flex flex-col shadow-sm">
                                    <div className="mb-8">
                                        <h3 className="text-2xl font-bold">{t.landing.pricing.free.title}</h3>
                                        <div className="mt-4 flex items-baseline">
                                            <span className="text-4xl font-extrabold tracking-tight">$0</span>
                                            <span className="ml-1 text-xl font-semibold text-slate-500 dark:text-slate-400">/ {t.landing.pricing.perMonth}</span>
                                        </div>
                                    </div>
                                    <ul className="space-y-4 mb-8 flex-1">
                                        {[
                                            { text: t.landing.pricing.features.oneCar, included: true },
                                            { text: t.landing.pricing.features.unlimitedExpenses, included: true },
                                            { text: t.landing.pricing.features.unlimitedSpareParts, included: true },
                                            { text: t.landing.pricing.features.unlimitedNotes, included: true },
                                            { text: t.landing.pricing.features.repeatedExpenses, included: true },
                                            { text: t.landing.pricing.features.emailReminders, included: true },
                                            { text: t.landing.pricing.features.basicStatistics, included: true },
                                            { text: t.landing.pricing.features.storage50mb, included: true },
                                            { text: t.landing.pricing.features.unlimitedArchivedCars, included: true },
                                        ].map((feature, i) => (
                                            <li key={i} className="flex items-start">
                                                <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mr-3 ${feature.included ? 'text-green-500 bg-green-50 dark:bg-green-900/20' : 'text-slate-300 dark:text-slate-700 bg-slate-100 dark:bg-slate-800'}`}>
                                                    {feature.included ? <Check size={12} strokeWidth={3} /> : <div className="w-1.5 h-1.5 rounded-full bg-current" />}
                                                </div>
                                                <span className={feature.included ? 'text-slate-900 dark:text-slate-200' : 'text-slate-500 dark:text-slate-500'}>{feature.text}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        onClick={() => navigate(user ? '/garage' : '/login')}
                                        className="w-full py-4 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-900 dark:text-white rounded-xl font-bold transition-all mt-auto"
                                    >
                                        {t.landing.nav.getStarted}
                                    </button>
                                </div>

                                {/* Pro Plan */}
                                <div className="border-2 border-blue-600 dark:border-blue-500 rounded-2xl p-8 bg-blue-50 dark:bg-blue-900/10 flex flex-col relative shadow-md">
                                    <div className="mb-8">
                                        <h3 className="text-2xl font-bold">{t.landing.pricing.pro.title}</h3>
                                        <div className="mt-4 flex items-baseline">
                                            <span className="text-4xl font-extrabold tracking-tight">$4.99</span>
                                            <span className="ml-1 text-xl font-semibold text-slate-500 dark:text-slate-400">/ {t.landing.pricing.perMonth}</span>
                                        </div>
                                    </div>
                                    <ul className="space-y-4 mb-8 flex-1">
                                        {[
                                            { text: t.landing.pricing.features.allInFreePlus, included: true, isHeader: true },
                                            { text: t.landing.pricing.features.threeCars, included: true },
                                            { text: t.landing.pricing.features.storage2gb.replace('2 Gb', '1 Gb').replace('2 Go', '1 Go').replace('2 Гб', '1 Гб'), included: true },
                                            { text: t.landing.pricing.features.emailParsing, included: true },
                                            { text: t.landing.pricing.features.carAdmins, included: true },
                                            { text: t.landing.pricing.features.fullStatistics, included: true },
                                            { text: t.landing.pricing.features.dataExport, included: true },
                                        ].map((feature, i) => (
                                            <li key={i} className="flex items-start">
                                                {feature.isHeader ? (
                                                    <span className="text-xs font-black uppercase tracking-widest text-blue-600 dark:text-blue-400 mt-1 mb-1">{feature.text}</span>
                                                ) : (
                                                    <>
                                                        <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mr-3 text-blue-600 bg-blue-100 dark:bg-blue-900/30">
                                                            <Check size={12} strokeWidth={3} />
                                                        </div>
                                                        <span className="text-slate-900 dark:text-slate-200 font-medium">{feature.text}</span>
                                                    </>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        onClick={() => navigate(user ? '/settings/plans' : '/login')}
                                        className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-all shadow-xl shadow-blue-500/20 mt-auto"
                                    >
                                        {t.landing.pricing.pro.cta || (t.settings.upgradePlan || 'Upgrade')}
                                    </button>
                                </div>

                                {/* Custom Plan */}
                                <div className="border border-slate-200 dark:border-slate-700 rounded-2xl p-8 bg-white dark:bg-slate-900 flex flex-col shadow-sm">
                                    <div className="mb-8">
                                        <h3 className="text-2xl font-bold">{t.landing.pricing.custom.title}</h3>
                                    </div>
                                    <ul className="space-y-4 mb-8 flex-1">
                                        {[
                                            { text: t.landing.pricing.features.moreCars, included: true },
                                            { text: t.landing.pricing.features.moreFileStorage, included: true },
                                            { text: t.landing.pricing.features.customRequirements, included: true },
                                        ].map((feature, i) => (
                                            <li key={i} className="flex items-start">
                                                <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mr-3 text-blue-600 bg-blue-50 dark:bg-blue-900/20">
                                                    <Check size={12} strokeWidth={3} />
                                                </div>
                                                <span className="text-slate-900 dark:text-slate-200">{feature.text}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        onClick={() => window.location.href = `mailto:${t.landing.footer.contactEmail}`}
                                        className="w-full py-4 bg-slate-900 dark:bg-slate-800 text-white rounded-xl font-bold hover:bg-slate-800 dark:hover:bg-slate-700 transition-all mt-auto"
                                    >
                                        {t.landing.pricing.custom.cta}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Repair or Sell Section */}
                <section className="py-16 relative overflow-hidden bg-white dark:bg-slate-950">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col lg:flex-row items-center gap-8">
                            <div className="flex-1 text-center lg:text-left">
                                <h2 className="text-3xl md:text-4xl font-black mb-4 leading-tight text-slate-900 dark:text-white">
                                    {t.landing.repairAnalysisSection.title}
                                </h2>
                                <h3 className="text-lg text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
                                    {t.landing.repairAnalysisSection.subtitle}
                                </h3>
                                
                                <div className="grid sm:grid-cols-1 gap-4 mb-6 text-left">
                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-10 h-10 bg-blue-50 dark:bg-blue-900/20 rounded-2xl flex items-center justify-center text-blue-600 dark:text-blue-400">
                                            <TrendingUp size={20} />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-base text-slate-900 dark:text-white">{t.landing.repairAnalysisSection.features.personalized.title}</h4>
                                            <p className="text-sm text-slate-600 dark:text-slate-400">{t.landing.repairAnalysisSection.features.personalized.description}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-10 h-10 bg-indigo-50 dark:bg-indigo-900/20 rounded-2xl flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                                            <Users size={20} />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-base text-slate-900 dark:text-white">{t.landing.repairAnalysisSection.features.public.title}</h4>
                                            <p className="text-sm text-slate-600 dark:text-slate-400">{t.landing.repairAnalysisSection.features.public.description}</p>
                                        </div>
                                    </div>
                                </div>

                                <button 
                                    onClick={() => navigate('/repair')}
                                    className="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-lg transition-all shadow-xl shadow-blue-500/20 inline-flex items-center gap-2 group"
                                >
                                    {t.landing.repairAnalysisSection.cta}
                                    <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                                </button>
                            </div>
                            
                            <div className="flex-1 w-full max-w-[320px] sm:max-w-[360px] mx-auto lg:mx-0">
                                <div className="relative group/phone w-full">
                                    <div className="absolute -inset-4 bg-gradient-to-tr from-emerald-500 to-blue-600 rounded-[3rem] blur-2xl opacity-20 dark:opacity-30 group-hover/phone:opacity-40 transition-opacity duration-700"></div>
                                    <div className="relative rounded-[3rem] border-8 border-slate-900 dark:border-slate-800 bg-gradient-theme shadow-3xl overflow-hidden aspect-[9/19] flex flex-col">
                                        <div className="h-10 px-6 flex items-center justify-between text-[11px] font-black z-30 text-slate-900 dark:text-white pointer-events-none shrink-0 bg-transparent">
                                            <span className="mt-1">14:28</span>
                                            <div className="flex items-center gap-1 mt-1">
                                                <div className="flex gap-0.5 items-end h-2.5">
                                                    <div className="w-0.5 h-1 bg-current opacity-40"></div>
                                                    <div className="w-0.5 h-1.5 bg-current"></div>
                                                    <div className="w-0.5 h-2 bg-current"></div>
                                                </div>
                                                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12.55a11 11 0 0 1 14.08 0" /><path d="M1.42 9a16 16 0 0 1 21.16 0" /><path d="M8.53 16.11a6 6 0 0 1 6.95 0" /><line x1="12" y1="20" x2="12.01" y2="20" /></svg>
                                                <div className="w-5 h-2.5 border border-current rounded-[2px] p-[1px] flex items-center">
                                                    <div className="flex-1 h-[100%] bg-current rounded-[0.5px]"></div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="absolute top-0 left-0 right-0 h-6 flex justify-center z-40 pointer-events-none">
                                            <div className="w-24 h-5 bg-slate-900 dark:bg-slate-800 rounded-b-2xl"></div>
                                        </div>
                                        <div className="flex-1 overflow-hidden">
                                            <RepairLandingPreview slug="280472aa23" />
                                        </div>
                                        <div className="absolute inset-0 pointer-events-none select-none z-10 bg-gradient-to-tr from-white/5 via-transparent to-white/5"></div>
                                        <div className="absolute bottom-1.5 left-0 right-0 h-1 flex justify-center z-30">
                                            <div className="w-1/3 h-1 bg-slate-400/20 rounded-full"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Mechanic Quote Auditor Lead Magnet Section */}
                <section className="py-24 relative overflow-hidden bg-slate-50 dark:bg-[#000D19]/40">
                    <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-200 dark:via-blue-900/30 to-transparent" />
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col items-center text-center max-w-3xl mx-auto">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-bold text-xs uppercase tracking-widest mb-6">
                                <Shield size={14} />
                                {t.audit.hero.badge}
                            </div>
                            <h2 className="text-3xl md:text-5xl font-black mb-6 tracking-tight leading-tight text-slate-900 dark:text-white">
                                {t.audit.hero.title}
                            </h2>
                            <p className="text-xl text-slate-600 dark:text-slate-400 mb-10 leading-relaxed font-medium">
                                {t.audit.hero.subtitle}
                            </p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 w-full text-left">
                                <div className="flex gap-4 p-6 bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800">
                                    <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-2xl flex items-center justify-center shrink-0 shadow-sm">
                                        <Check size={24} strokeWidth={3} />
                                    </div>
                                    <div>
                                        <h4 className="font-black text-lg text-slate-900 dark:text-white mb-1">{t.audit.valueProps.negotiation.title}</h4>
                                        <p className="text-sm text-slate-600 dark:text-slate-400 font-medium leading-relaxed">{t.audit.valueProps.negotiation.desc}</p>
                                    </div>
                                </div>
                                <div className="flex gap-4 p-6 bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800">
                                    <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-2xl flex items-center justify-center shrink-0 shadow-sm">
                                        <Check size={24} strokeWidth={3} />
                                    </div>
                                    <div>
                                        <h4 className="font-black text-lg text-slate-900 dark:text-white mb-1">{t.audit.valueProps.peace.title}</h4>
                                        <p className="text-sm text-slate-600 dark:text-slate-400 font-medium leading-relaxed">{t.audit.valueProps.peace.desc}</p>
                                    </div>
                                </div>
                                <div className="flex gap-4 p-6 bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800">
                                    <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-2xl flex items-center justify-center shrink-0 shadow-sm">
                                        <Check size={24} strokeWidth={3} />
                                    </div>
                                    <div>
                                        <h4 className="font-black text-lg text-slate-900 dark:text-white mb-1">{t.audit.valueProps.scams.title}</h4>
                                        <p className="text-sm text-slate-600 dark:text-slate-400 font-medium leading-relaxed">{t.audit.valueProps.scams.desc}</p>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={() => navigate('/audit')}
                                className="px-10 py-5 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-black text-xl transition-all shadow-2xl shadow-blue-500/20 flex items-center justify-center gap-3 group w-full sm:w-auto hover:scale-105 active:scale-95"
                            >
                                {t.audit.cta.button}
                                <ArrowRight size={24} className="group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                    </div>
                </section>



                {/* Founder's Note Section */}
                <section className="py-24 bg-white dark:bg-slate-950 border-t border-slate-100 dark:border-slate-900">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="relative p-10 md:p-16 rounded-[3rem] bg-slate-50 dark:bg-slate-900 overflow-hidden group">
                            <div className="absolute top-0 right-0 p-8 text-slate-200 dark:text-slate-800 pointer-events-none group-hover:scale-110 transition-transform">
                                <FileText size={120} strokeWidth={0.5} />
                            </div>
                            <div className="relative z-10">
                                <div className="flex-1">
                                    <h2 className="text-2xl font-black mb-6 text-slate-900 dark:text-white flex items-center gap-2">
                                        {t.landing.foundersNote.title}
                                        <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800 hidden md:block ml-4"></div>
                                    </h2>
                                    <p className="text-xl text-slate-600 dark:text-slate-300 italic font-medium leading-relaxed">
                                        "{t.landing.foundersNote.content}"
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Latest from the Blog (Temporarily Hidden) */}
                {false && (
                <section id="blog" className="py-24 bg-slate-50 dark:bg-slate-900/30">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col md:flex-row items-end justify-between gap-8 mb-16">
                            <div className="max-w-2xl text-center md:text-left">
                                <h2 className="text-3xl md:text-5xl font-black mb-4 tracking-tight">
                                    {t.blog.latestPosts}
                                </h2>
                                <p className="text-lg text-slate-600 dark:text-slate-400 font-medium">
                                    {t.blog.subtitle}
                                </p>
                            </div>
                            <button 
                                onClick={() => navigate('/blog')}
                                className="flex items-center gap-2 text-blue-600 dark:text-blue-400 font-black text-xl hover:gap-4 transition-all"
                            >
                                {t.blog.morePosts}
                                <ArrowRight size={24} />
                            </button>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {blogPosts.slice(0, 3).map((post) => (
                                <div 
                                    key={post.id}
                                    onClick={() => navigate(`/blog/${post.slug}`)}
                                    className="group cursor-pointer flex flex-col bg-white dark:bg-slate-900 rounded-[3rem] border border-slate-200 dark:border-slate-800 overflow-hidden hover:shadow-2xl transition-all duration-500"
                                >
                                    <div className="aspect-video bg-slate-100 dark:bg-slate-800 relative overflow-hidden">
                                        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-600/20 flex items-center justify-center text-blue-500">
                                            <span className="text-4xl font-black">{post.title[0]}</span>
                                        </div>
                                    </div>
                                    <div className="p-8 flex flex-col flex-1">
                                        <div className="flex items-center gap-3 text-sm font-bold text-slate-500 dark:text-slate-500 mb-4">
                                            <Calendar size={14} />
                                            {new Date(post.date).toLocaleDateString(language === 'lv' ? 'lv-LV' : 'en-US', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })}
                                        </div>
                                        <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-4 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors leading-tight">
                                            {post.title}
                                        </h3>
                                        <p className="text-slate-600 dark:text-slate-400 line-clamp-2 font-medium mb-6 flex-1">
                                            {post.excerpt}
                                        </p>
                                        <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 font-black group-hover:gap-3 transition-all">
                                            {t.blog.readArticle}
                                            <ArrowRight size={18} />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
                )}

                {/* CTA Section */}
                <section className="py-24 relative overflow-hidden">
                    <div className="absolute inset-0 bg-blue-600 dark:bg-blue-900/40">
                        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }}></div>
                    </div>
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
                        <h2 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight">{t.landing.cta.title}</h2>
                        <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto font-medium opacity-90">
                            {t.landing.cta.subtitle}
                        </p>
                        <div className="flex flex-col items-center gap-4">
                            <button
                                onClick={() => navigate(user ? '/' : '/login')}
                                className="px-10 py-4 bg-white text-blue-600 rounded-xl font-black text-lg hover:bg-blue-50 transition-all shadow-2xl hover:scale-105 active:scale-95"
                            >
                                {user ? 'Open My Garage' : t.landing.cta.button}
                            </button>
                            <p className="text-blue-100 text-sm font-bold opacity-80">
                                {t.landing.cta.note}
                            </p>
                        </div>
                    </div>
                </section>
            </main>

            <footer className="border-t border-slate-200 dark:border-slate-800 bg-[#EEF5FB]/80 dark:bg-[#000D19]/80 backdrop-blur-md py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 gap-12">
                    <div className="col-span-1">
                        <div className="flex items-center gap-3 mb-4">
                            <img src={isDark ? faviconDark : favicon} alt="Logo" className="w-8 h-8 object-contain" />
                            <span className="font-bold text-xl text-slate-900 dark:text-white">My Car Costs</span>
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            © 2026 My Car Costs
                        </p>
                    </div>

                    <div className="col-span-1">
                        <h4 className="font-bold mb-4 text-slate-900 dark:text-white uppercase tracking-wider text-xs opacity-80">{t.landing.footer.contact}</h4>
                        <div className="space-y-4">
                            <a href={`mailto:${t.landing.footer.contactEmail}`} className="block text-sm text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                {t.landing.footer.contactEmail}
                            </a>
                            <Link to="/privacy" className="block text-sm text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                {t.landing.footer.privacy}
                            </Link>
                            <Link to="/terms" className="block text-sm text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                {(t.landing as any).terms?.title || 'Terms and Conditions'}
                            </Link>
                            <Link to="/alternatives" className="block text-sm text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                {language === 'lv' ? 'Alternatīvas' : 'Alternatives'}
                            </Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}

function FeatureCard({ icon, title, description, color }: { icon: React.ReactNode, title: string, description: string, color: string }) {
    const colorClasses: Record<string, string> = {
        blue: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20',
        orange: 'text-orange-600 bg-orange-50 dark:bg-orange-900/20',
        purple: 'text-purple-600 bg-purple-50 dark:bg-purple-900/20',
        green: 'text-green-600 bg-green-50 dark:bg-green-900/20',
        red: 'text-red-600 bg-red-50 dark:bg-red-900/20',
        indigo: 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/20',
        cyan: 'text-cyan-600 bg-cyan-50 dark:bg-cyan-900/20',
        yellow: 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20',
        rose: 'text-rose-600 bg-rose-50 dark:bg-rose-900/20',
    };

    return (
        <div className="p-8 bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-xl transition-all group flex flex-col items-start gap-4">
            <div className={`w-14 h-14 shrink-0 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform ${colorClasses[color] || 'text-slate-600 bg-slate-50'}`}>
                {icon}
            </div>
            <div>
                <h3 className="text-xl font-bold mb-2 text-slate-900 dark:text-white leading-tight">{title}</h3>
                <p className="text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                    {description}
                </p>
            </div>
        </div>
    );
}
